import React, { useState } from "react";
import axios from "axios";
import { Mail, MapPin, Phone, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. Our team will be in touch shortly.");
      setSuccess(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Unable to send your message. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Unable to send your message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="page-fade">
      <Toaster theme="dark" position="top-right" richColors />

      <PageHero
        eyebrow="Get in Touch"
        title={<>Speak with <span className="italic">EMIVIA.</span></>}
        lede="Discuss your investment goals, mandates or partnership opportunities with our senior team. All enquiries are handled with absolute discretion."
        image="https://images.pexels.com/photos/14523757/pexels-photo-14523757.jpeg"
        testId="contact-hero"
      />

      <section className="bg-[#070B14] py-24 sm:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Info */}
          <Reveal className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] hairline">Registered Office</div>
            <h2 className="mt-6 font-serif-display text-white text-3xl sm:text-4xl tracking-tight leading-tight">
              EMIVIA LIMITED
            </h2>
            <p className="mt-4 text-white/60 text-sm uppercase tracking-[0.2em]">
              Company No. 13413474 · Incorporated 21 May 2021
            </p>

            <ul className="mt-12 space-y-8">
              <li className="flex gap-4">
                <span className="w-10 h-10 inline-flex items-center justify-center border border-[#D4AF37]/40">
                  <MapPin size={18} className="text-[#D4AF37]" strokeWidth={1.5} />
                </span>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.28em] text-white/50">Address</div>
                  <div className="mt-2 text-white/85 font-light leading-relaxed">
                    Tall Timbers, 2 Pine Glade,
                    <br />
                    Orpington, England, BR6 8NT
                    <br />
                    United Kingdom
                  </div>
                </div>
              </li>

              <li className="flex gap-4">
                <span className="w-10 h-10 inline-flex items-center justify-center border border-[#D4AF37]/40">
                  <Mail size={18} className="text-[#D4AF37]" strokeWidth={1.5} />
                </span>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.28em] text-white/50">Email</div>
                  <a
                    href="mailto:contact@emivia-limited.com"
                    data-testid="contact-info-email"
                    className="mt-2 inline-block text-white/85 font-light hover:text-[#D4AF37] transition-colors"
                  >
                    contact@emivia-limited.com
                  </a>
                </div>
              </li>

              <li className="flex gap-4">
                <span className="w-10 h-10 inline-flex items-center justify-center border border-[#D4AF37]/40">
                  <Phone size={18} className="text-[#D4AF37]" strokeWidth={1.5} />
                </span>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.28em] text-white/50">CEO</div>
                  <div className="mt-2 text-white/85 font-light">Siri Algeroy</div>
                </div>
              </li>
            </ul>

            <div className="mt-16 border border-white/10 bg-[#0B1120] p-8">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">Confidentiality</div>
              <p className="mt-3 text-white/65 text-sm font-light leading-relaxed">
                All enquiries are treated with strict confidentiality. Our team typically responds within one business day.
              </p>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={120} className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="border border-white/10 bg-[#0B1120] p-8 sm:p-12 space-y-8"
            >
              <div>
                <div className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]">Send a Message</div>
                <h3 className="mt-3 font-serif-display text-white text-3xl tracking-tight">
                  Tell us about your needs.
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Full name" name="name" value={form.name} onChange={onChange} testId="contact-name" required />
                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} testId="contact-email" required />
                <div className="sm:col-span-2">
                  <Field label="Subject" name="subject" value={form.subject} onChange={onChange} testId="contact-subject" required />
                </div>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.28em] text-white/50 mb-3">Message</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  required
                  rows={6}
                  data-testid="contact-message"
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#D4AF37] outline-none text-white font-light py-3 resize-none transition-colors"
                  placeholder="Briefly describe your enquiry…"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-2">
                <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 max-w-sm">
                  By submitting this form, you agree your information will be processed by EMIVIA LIMITED in accordance with our privacy practices.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="inline-flex items-center justify-center gap-3 text-[11px] uppercase tracking-[0.32em] bg-[#D4AF37] text-[#070B14] px-8 py-4 hover:bg-[#F3CE52] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed min-w-[200px]"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Sending
                    </>
                  ) : success ? (
                    <>
                      <CheckCircle2 size={14} /> Sent
                    </>
                  ) : (
                    <>
                      Send message <Send size={13} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, required, testId }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.28em] text-white/50 mb-3">
        {label}
        {required && <span className="text-[#D4AF37] ml-1">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="w-full bg-transparent border-b border-white/15 focus:border-[#D4AF37] outline-none text-white font-light py-3 transition-colors"
      />
    </div>
  );
}
